import React, { useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import axios from "axios";
import randomString from "@/lib/utils/randomString";
import { useThemeStore } from "@/store";

interface CKEditorWithFileUploadProps {
  value?: string;
  onChange?: (data: string) => void;
  isPublic?: boolean;
}

const CKEditorWithFileUpload: React.FC<CKEditorWithFileUploadProps> = ({
  value = "",
  onChange,
  isPublic = false,
}) => {
  const theme = useThemeStore((state) => state.theme);
  const folderName = "uploads";
  const editorRef = useRef<any>(null);

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute("content") || "" : "";
  };

  const uploadFileToS3 = async (file: File): Promise<string | null> => {
    try {
      const csrfToken = getCsrfToken();
      const fileNameParts = file.name.split(".");
      const fileExtension = fileNameParts.length > 1 ? fileNameParts.pop() : "";
      const fileNameWithoutExtension = fileNameParts.join(".");
      const fileName = `${fileNameWithoutExtension}${randomString(6)}.${fileExtension}`;

      const response = await axios.post(
        "/attachments/presigned_url",
        {
          file_name: fileName,
          file_type: file.type,
          folder_name: folderName,
          is_public: isPublic,
        },
        {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      const presignedUrl: string = response.data.url;

      await axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
      
      const domain = new URL(presignedUrl).origin;
  
      return `${domain}/${folderName}/${fileName}`; 
    } catch (error) {
      console.error("File upload error:", error);
      return null;
    }
  };

  const CustomUploadAdapterPlugin = function (editor: any) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader: any) => {
      return {
        upload: async () => {
          return new Promise(async (resolve, reject) => {
            const file = await loader.file;
            const url = await uploadFileToS3(file);
            if (url) {
              resolve({ default: url });
            } else {
              reject("Upload failed");
            }
          });
        },
      };
    };
  };

  return (
    <div className={`min-h-[300px] p-4 rounded-md shadow-md transition-colors ${theme === "dark" ? "dark-mode": ""}`}>
      <CKEditor
      editor={ClassicEditor}
      data={value}
      onReady={(editor) => {
        editorRef.current = editor;
      }}
      config={{
        toolbar: [
          "heading",
          "|",
          "bold",
          "italic",
          "link",
          "bulletedList",
          "numberedList",
          "|",
          "imageUpload",
          "blockQuote",
          "undo",
          "redo",
        ],
        extraPlugins: [CustomUploadAdapterPlugin], 
        
      }}
      onChange={(_, editor) => {
        const data = editor.getData();
        if (onChange) {
          onChange(data);
        }
      }}
    />
    </div>
    
  );
};

export default CKEditorWithFileUpload;
